import { Fragment, useMemo, useState } from 'react';
import { Stat } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatNumber, num, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

interface Contract {
  id: number;
  name: string;
  number: string | null;
  client: string | null;
  type: string | null;
  category: string | null;
  status: string | null;
  start: string | null;
  end: string | null;
  period: string | null;
  billingPreference: string | null;
  sla: string | null;
  isDefault: boolean;
  overageRate: number | null;
  setupFee: number | null;
  estimatedHours: number | null;
  estimatedRevenue: number | null;
  exclusionContract: string | null;
  exclusionSet: { name: string; description: string | null; roles: string[]; workTypes: string[] } | null;
  excludedRoles: string[];
  excludedWorkTypes: string[];
  roleRates: { role: string; rate: number | null; standard: number | null }[];
  roleCosts: { role: string; resource: string | null; cost: number | null }[];
  services: { name: string; unitPrice: number | null; unitCost: number | null }[];
  issueTypes: { issue: string; count: number }[];
  tickets: number;
}
interface Payload {
  from: string;
  to: string;
  status: 'active' | 'all';
  fetchedUtc: string;
  contracts: Contract[];
  unavailable: { entity: string; reason: string }[];
  fields: Record<string, Record<string, string | null>>;
}

const money = (v: number | null) => (v === null ? '—' : formatNumber(v, 'currency'));
const day = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const daysBack = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - (n - 1));
  return toLocalDateString(d);
};
const list = (items: string[]) => (items.length ? items.join(', ') : '—');

/**
 * Reports › Contract Setup — how every Autotask contract is set up: its
 * terms, the exclusion set and the roles and work types it excludes, the
 * contract's own excluded roles and work types, role rates and costs,
 * services, and the issue types of the tickets logged against it in the
 * period chosen (routes/reportsContractSetup.ts).
 */
export function ContractSetupReportPage() {
  const [status, setStatus] = useState<'active' | 'all'>('active');
  const [{ from, to }, setRange] = useState(() => ({ from: daysBack(90), to: todayLocal() }));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [onlyExclusions, setOnlyExclusions] = useState(false);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const data = useHeldResource<Payload>(`/reports/contract-setup?${new URLSearchParams({ from, to, status })}`);
  const d = data.data;

  const types = useMemo(() => [...new Set((d?.contracts ?? []).map((c) => c.type ?? 'No type'))].sort(), [d]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (d?.contracts ?? []).filter(
      (c) =>
        (!type || (c.type ?? 'No type') === type) &&
        (!onlyExclusions || c.exclusionSet || c.excludedRoles.length || c.excludedWorkTypes.length) &&
        (!q || [c.name, c.client, c.number, c.exclusionSet?.name].some((v) => v && v.toLowerCase().includes(q))),
    );
  }, [d, search, type, onlyExclusions]);
  const toggle = (id: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const exportCsv = () =>
    downloadCsv(`contract_setup_${fileStamp()}.csv`, [
      [
        'Client', 'Contract', 'Number', 'Type', 'Category', 'Status', 'Start', 'End', 'Period', 'Billing preference', 'SLA', 'Default',
        'Overage rate', 'Exclusion set', 'Set excluded roles', 'Set excluded work types', 'Contract excluded roles',
        'Contract excluded work types', 'Excluded time bills to', 'Role rates', 'Services', `Issue types (${from} to ${to})`,
      ],
      ...rows.map((c) => [
        c.client, c.name, c.number, c.type, c.category, c.status, c.start, c.end, c.period, c.billingPreference, c.sla, c.isDefault ? 'Yes' : 'No',
        c.overageRate ?? '', c.exclusionSet?.name ?? '', c.exclusionSet?.roles.join('; ') ?? '', c.exclusionSet?.workTypes.join('; ') ?? '',
        c.excludedRoles.join('; '), c.excludedWorkTypes.join('; '), c.exclusionContract ?? '',
        c.roleRates.map((r) => `${r.role} ${r.rate ?? ''}`).join('; '), c.services.map((s) => s.name).join('; '),
        c.issueTypes.map((i) => `${i.issue} (${i.count})`).join('; '),
      ]),
    ]);

  const withSet = rows.filter((c) => c.exclusionSet).length;
  const withOwn = rows.filter((c) => c.excludedRoles.length || c.excludedWorkTypes.length).length;
  const withRates = rows.filter((c) => c.roleRates.length).length;

  return (
    <>
      <PageHead
        title="Contract Setup"
        lede="How every contract is set up in Autotask — exclusion sets and the work roles and work types they exclude, the contract's own exclusions, role rates, services, and the issue types of its tickets."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Contracts">
              <select className="select" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
                <option value="active">Active contracts</option>
                <option value="all">All contracts</option>
              </select>
            </Field>
            <Field label="Contract type">
              <select className="select" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">All types</option>
                {types.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Search">
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Client, contract or exclusion set" />
            </Field>
            <label className="checkbox">
              <input type="checkbox" checked={onlyExclusions} onChange={(e) => setOnlyExclusions(e.target.checked)} />
              Only contracts with exclusions
            </label>
          </div>
          <div className="row wrap mt-3" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Issue types from tickets created">
              <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
            </Field>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
              onClick={() => setRange({ from: draftFrom, to: draftTo })}
            >
              Show
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!rows.length} onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          <p className="xs muted mt-2">
            Autotask has no issue-type setting on a contract, so issue types are those of the tickets logged against each contract, created{' '}
            {day(from)} – {day(to)}.
          </p>
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {d && d.unavailable.length > 0 && (
        <ErrorBanner message={`Not included: ${d.unavailable.map((u) => `${u.entity} (${u.reason})`).join('; ')}.`} />
      )}
      {data.loading && !d && <Loading label="Reading contracts and their setup from Autotask" />}

      {d && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Contracts" value={num(rows.length)} foot={status === 'active' ? 'active' : 'all statuses'} />
            <Stat label="With an exclusion set" value={num(withSet)} foot="reusable set of exclusions" />
            <Stat label="With their own exclusions" value={num(withOwn)} foot="roles or work types on the contract" />
            <Stat label="With role rates" value={num(withRates)} foot="override the standard rate" />
          </div>

          <section className="card">
            <div className="table-wrap">
              <table className="table cs-table" data-nosort>
                <thead>
                  <tr>
                    <th>Client / contract</th>
                    <th>Type</th>
                    <th>Exclusion set</th>
                    <th>Excluded roles</th>
                    <th>Excluded work types</th>
                    <th>Excluded time bills to</th>
                    <th>Issue types (tickets)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="muted small">
                        No contracts match.
                      </td>
                    </tr>
                  )}
                  {rows.map((c) => {
                    const isOpen = open.has(c.id);
                    const roles = [...(c.exclusionSet?.roles ?? []), ...c.excludedRoles];
                    const workTypes = [...(c.exclusionSet?.workTypes ?? []), ...c.excludedWorkTypes];
                    return (
                      <Fragment key={c.id}>
                        <tr className={isOpen ? 'is-open' : undefined}>
                          <td>
                            <div className="xs muted">{c.client ?? '—'}</div>
                            <button type="button" className="link-button" onClick={() => toggle(c.id)} aria-expanded={isOpen}>
                              {isOpen ? '▾' : '▸'} {c.name}
                            </button>
                            {c.isDefault && <span className="badge badge-neutral" style={{ marginLeft: 6 }}>default</span>}
                          </td>
                          <td className="xs">
                            {c.type ?? '—'}
                            {c.status && c.status !== 'Active' && <div className="muted">{c.status}</div>}
                          </td>
                          <td className="xs">{c.exclusionSet?.name ?? '—'}</td>
                          <td className="xs">{list([...new Set(roles)])}</td>
                          <td className="xs">{list([...new Set(workTypes)])}</td>
                          <td className="xs">{c.exclusionContract ?? '—'}</td>
                          <td className="xs">
                            {c.issueTypes.length
                              ? c.issueTypes
                                  .slice(0, 3)
                                  .map((i) => `${i.issue} (${i.count})`)
                                  .join(', ') + (c.issueTypes.length > 3 ? ` +${c.issueTypes.length - 3} more` : '')
                              : '—'}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="cs-detail">
                            <td colSpan={7}>
                              <ContractDetail c={c} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
          <p className="xs muted mt-2">
            Read {new Date(d.fetchedUtc).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}. Linked by the Autotask
            fields{' '}
            {Object.entries(d.fields)
              .map(([k, v]) => `${k}: ${Object.values(v).filter(Boolean).join(', ') || 'none found'}`)
              .join(' · ')}
            .
          </p>
        </>
      )}
    </>
  );
}

function ContractDetail({ c }: { c: Contract }) {
  return (
    <div className="cs-grid">
      <div>
        <h4>Terms</h4>
        <dl className="cs-dl">
          <dt>Number</dt>
          <dd>{c.number ?? '—'}</dd>
          <dt>Type / category</dt>
          <dd>
            {c.type ?? '—'}
            {c.category ? ` · ${c.category}` : ''}
          </dd>
          <dt>Status</dt>
          <dd>{c.status ?? '—'}</dd>
          <dt>Dates</dt>
          <dd>
            {day(c.start)} – {c.end ? day(c.end) : 'no end date'}
          </dd>
          <dt>Period</dt>
          <dd>{c.period ?? '—'}</dd>
          <dt>Billing preference</dt>
          <dd>{c.billingPreference ?? '—'}</dd>
          <dt>SLA</dt>
          <dd>{c.sla ?? '—'}</dd>
          <dt>Default contract</dt>
          <dd>{c.isDefault ? 'Yes' : 'No'}</dd>
          <dt>Overage rate</dt>
          <dd>{money(c.overageRate)}</dd>
          <dt>Setup fee</dt>
          <dd>{money(c.setupFee)}</dd>
          <dt>Estimates</dt>
          <dd>
            {c.estimatedHours === null ? '—' : `${num(c.estimatedHours)} h`} · {money(c.estimatedRevenue)}
          </dd>
        </dl>
      </div>
      <div>
        <h4>Exclusions</h4>
        <dl className="cs-dl">
          <dt>Exclusion set</dt>
          <dd>
            {c.exclusionSet ? (
              <>
                <strong>{c.exclusionSet.name}</strong>
                {c.exclusionSet.description && <div className="xs muted">{c.exclusionSet.description}</div>}
              </>
            ) : (
              '—'
            )}
          </dd>
          <dt>Set excludes roles</dt>
          <dd>{list(c.exclusionSet?.roles ?? [])}</dd>
          <dt>Set excludes work types</dt>
          <dd>{list(c.exclusionSet?.workTypes ?? [])}</dd>
          <dt>Contract excludes roles</dt>
          <dd>{list(c.excludedRoles)}</dd>
          <dt>Contract excludes work types</dt>
          <dd>{list(c.excludedWorkTypes)}</dd>
          <dt>Excluded time bills to</dt>
          <dd>{c.exclusionContract ?? '— (the client’s default contract, or none)'}</dd>
        </dl>
      </div>
      <div>
        <h4>Role rates</h4>
        {c.roleRates.length === 0 ? (
          <p className="xs muted">Standard role rates.</p>
        ) : (
          <table className="table cs-mini" data-nosort>
            <tbody>
              {c.roleRates.map((r) => (
                <tr key={r.role}>
                  <td>{r.role}</td>
                  <td className="num">{money(r.rate)}</td>
                  <td className="num xs muted">standard {money(r.standard)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {c.roleCosts.length > 0 && (
          <>
            <h4 className="mt-3">Role costs</h4>
            <table className="table cs-mini" data-nosort>
              <tbody>
                {c.roleCosts.map((r, i) => (
                  <tr key={i}>
                    <td>
                      {r.role}
                      {r.resource && <span className="xs muted"> · {r.resource}</span>}
                    </td>
                    <td className="num">{money(r.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <h4 className="mt-3">Services</h4>
        {c.services.length === 0 ? (
          <p className="xs muted">None.</p>
        ) : (
          <table className="table cs-mini" data-nosort>
            <tbody>
              {c.services.map((s, i) => (
                <tr key={i}>
                  <td>{s.name}</td>
                  <td className="num">{money(s.unitPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div>
        <h4>Issue types ({num(c.tickets)} tickets)</h4>
        {c.issueTypes.length === 0 ? (
          <p className="xs muted">No tickets on this contract in these dates.</p>
        ) : (
          <table className="table cs-mini" data-nosort>
            <tbody>
              {c.issueTypes.map((i) => (
                <tr key={i.issue}>
                  <td>{i.issue}</td>
                  <td className="num">{num(i.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
